import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import axios from 'axios';

const API_BASE_URL = 'https://genz-staging.feeltiptop.com';

const DoctorOnboard = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ open: false, success: true, message: '' });

  useEffect(() => {
    fetchUsers();
  }, []);

  const getAuthHeaders = () => ({
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      'Content-Type': 'application/json'
    }
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/users`, getAuthHeaders());
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (err) {
      setSubmitStatus({
        open: true,
        success: false,
        message: 'Failed to fetch users. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      setSubmitStatus({
        open: true,
        success: false,
        message: 'Please select a user to onboard as a doctor.'
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/doctor/onboard`,
        { userId: selectedUser.userId },
        getAuthHeaders()
      );

      if (response.data.success) {
        setSubmitStatus({
          open: true,
          success: true,
          message: 'Doctor successfully onboarded!'
        });
        setSelectedUser(null);
      } else {
        throw new Error(response.data.message || 'Failed to onboard doctor');
      }
    } catch (err) {
      setSubmitStatus({
        open: true,
        success: false,
        message: err.response?.data?.message || 'Failed to onboard doctor. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!localStorage.getItem('authToken')) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Please log in to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, margin: '0 auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Doctor Onboarding
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3 }}>
          Select a user to onboard as a doctor for speech assessments.
        </Typography>

        <form onSubmit={handleSubmit}>
          <Autocomplete
            options={users}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
            value={selectedUser}
            onChange={(event, newValue) => setSelectedUser(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select User"
                required
                fullWidth
                margin="normal"
              />
            )}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading || !selectedUser}
          >
            {loading ? <CircularProgress size={24} /> : 'Onboard as Doctor'}
          </Button>
        </form>

        <Snackbar
          open={submitStatus.open}
          autoHideDuration={6000}
          onClose={() => setSubmitStatus({ ...submitStatus, open: false })}
        >
          <Alert
            severity={submitStatus.success ? 'success' : 'error'}
            sx={{ width: '100%' }}
          >
            {submitStatus.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default DoctorOnboard; 